import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, chapterList } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comicId = Number(id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [comicId]);

  const comic = useMemo(() => comics.find(c => c.id === comicId), [comicId]);

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter(c => c.id !== comic.id && c.category === comic.category)
      .slice(0, 6);
  }, [comic]);

  useEffect(() => {
    if (comic) {
      document.title = `${comic.title}漫画 - 八仔漫画网在线免费阅读`;
    }
  }, [comic]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-300"></i>
            <p className="mt-4 text-foreground-500">漫画未找到</p>
            <Link to="/" className="inline-block mt-3 text-sm text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: comic.category ? '分类' : '', href: '/category/all' },
    { label: comic.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title}漫画全集在线免费阅读`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2026-01-01',
    dateModified: comic.updateTime,
    publisher: {
      '@type': 'Organization',
      name: '八仔漫画网',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <article className="bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-6 mb-8">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="w-full md:w-64 shrink-0">
                    <div className="aspect-[3/4] rounded-lg overflow-hidden">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} 漫画封面`}
                        title={`${comic.title} - 八仔漫画网`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                      {comic.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-foreground-500">
                      <span>作者：{comic.author}</span>
                      <span className="text-foreground-300">|</span>
                      <span>画师：{comic.artist}</span>
                      <span className="text-foreground-300">|</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-700'
                      }`}>
                        {comic.status}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 mt-3">
                      <span className="flex items-center gap-1 text-sm">
                        <i className="ri-star-fill text-accent-500"></i>
                        <strong className="text-foreground-800">{comic.rating}</strong>
                        <span className="text-foreground-500">分</span>
                      </span>
                      <span className="text-sm text-foreground-500">
                        {comic.popularity > 1000000
                          ? `${(comic.popularity / 10000).toFixed(0)}万人气`
                          : `${(comic.popularity / 10000).toFixed(1)}万人气`}
                      </span>
                      <span className="text-sm text-foreground-500">共{comic.chapters}话</span>
                      <span className="text-sm text-foreground-500">更新于 {comic.updateTime}</span>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-4">
                      {comic.tags.map((tag) => (
                        <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>

                    <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                      {comic.description}
                    </p>

                    <div className="flex flex-wrap gap-3 mt-5">
                      <Link
                        to={`/article/${comic.id}`}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-play-circle-line"></i>
                        开始阅读第一话
                      </Link>
                      <button className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap">
                        <i className="ri-bookmark-line"></i>
                        收藏漫画
                      </button>
                    </div>
                  </div>
                </div>
              </article>

              <section className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900">
                    章节目录
                  </h2>
                  <span className="text-sm text-foreground-500">共{chapterList.length}话</span>
                </div>

                <div className="bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <button className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 whitespace-nowrap">
                      正序
                    </button>
                    <button className="px-3 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-500 whitespace-nowrap">
                      倒序
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[500px] overflow-y-auto pr-2">
                    {chapterList.map((ch) => (
                      <Link
                        key={ch.id}
                        to={`/article/${comic.id}`}
                        className="flex items-center justify-between px-3 py-2 rounded-lg bg-background-100 hover:bg-primary-50 text-sm text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap"
                      >
                        <span>{ch.title}</span>
                        <span className="text-xs text-foreground-400">{ch.updateTime}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </section>

              {relatedComics.length > 0 && (
                <section className="pb-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
                    <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900">
                      相关推荐
                    </h2>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                    {relatedComics.map((rc) => (
                      <ComicCard
                        key={rc.id}
                        id={rc.id}
                        title={rc.title}
                        author={rc.author}
                        cover={rc.cover}
                        rating={rc.rating}
                        status={rc.status}
                        latestChapter={rc.latestChapter}
                        tags={rc.tags}
                      />
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}